import { Controller } from '@hotwired/stimulus';
import {
  MARKER,
  TOKEN_REGEX,
  convertExistingTokens,
  encodeSequence,
  escapeHtml as escapeHtmlUtil,
  replacePlaceholdersWithTokens
} from '../utils/mention_tokens';

export default class extends Controller {
  static targets = ['display', 'hidden', 'highlight', 'suggestions'];
  static values = {
    url: String,
    placeholder: String
  };

  connect() {
    this.mentionMap = new Map();
    this.sequenceRef = { value: 0 };
    this.activeIndex = -1;
    this.suggestions = [];
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    document.addEventListener('click', this.handleDocumentClick);

    this.initializeDisplay();
  }

  disconnect() {
    document.removeEventListener('click', this.handleDocumentClick);
    this.clearFetchTimer();

    if (this.hasSuggestionsTarget && this.boundSuggestionClick) {
      this.suggestionsTarget.removeEventListener('click', this.boundSuggestionClick);
      this.boundSuggestionClick = null;
    }
  }

  handleInput(event) {
    if (!this.hasDisplayTarget) {
      return;
    }

    this.pruneMentionMap();
    this.observeQuery();
    this.syncHiddenFromDisplay();
  }

  handleKeydown(event) {
    if (this.handleMentionDeletion(event)) {
      this.syncHiddenFromDisplay();
      return;
    }

    if (!this.isOpen()) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveSelection(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveSelection(-1);
        break;
      case 'Enter':
        event.preventDefault();
        this.chooseActiveSuggestion();
        break;
      case 'Tab':
        this.chooseActiveSuggestion();
        break;
      case 'Escape':
        this.hideSuggestions();
        break;
      default:
    }
  }

  beforeSubmit() {
    if (!this.hasDisplayTarget) {
      return;
    }

    this.pruneMentionMap();
    this.hideSuggestions();
    this.syncHiddenFromDisplay();
  }

  handleSuggestionClick(event) {
    const button = event.target.closest('[data-index]');
    if (!button) {
      return;
    }
    event.preventDefault();
    const index = Number.parseInt(button.dataset.index, 10);
    this.insertMentionFromIndex(index);
  }

  handleDocumentClick(event) {
    if (!this.element.contains(event.target)) {
      this.hideSuggestions();
    }
  }

  // Internal helpers

  initializeDisplay() {
    if (!this.hasDisplayTarget) {
      return;
    }

    const initialValue = this.hasHiddenTarget ? this.hiddenTarget.value || '' : '';
    const transformed = convertExistingTokens(initialValue, this.mentionMap, this.sequenceRef);
    this.displayTarget.value = transformed;
    this.syncHiddenFromDisplay();
  }

  observeQuery() {
    if (!this.hasDisplayTarget) {
      return;
    }

    const cursor = this.displayTarget.selectionStart;
    const textBefore = this.displayTarget.value.slice(0, cursor);
    const match = textBefore.match(/(?:^|\s)@([^\s@]*)$/);

    if (!match) {
      this.hideSuggestions();
      return;
    }

    const query = match[1];
    const start = cursor - query.length - 1;
    this.activeMentionRange = { start, end: cursor };

    this.scheduleFetch(query);
  }

  scheduleFetch(query) {
    this.clearFetchTimer();

    const trimmed = query.trim();
    this.pendingQuery = trimmed;

    this.fetchTimer = window.setTimeout(() => {
      this.performFetch(trimmed);
    }, 150);
  }

  clearFetchTimer() {
    if (this.fetchTimer) {
      clearTimeout(this.fetchTimer);
      this.fetchTimer = null;
    }
  }

  performFetch(query) {
    if (!this.hasUrlValue) {
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    const url = new URL(this.urlValue, window.location.origin);
    if (query) {
      url.searchParams.set('q', query);
    }

    fetch(url.toString(), {
      signal: this.abortController.signal,
      headers: { Accept: 'application/json' }
    })
      .then((response) => response.json())
      .then((data) => {
        this.renderSuggestions(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Mention fetch failed', error);
        }
      });
  }

  renderSuggestions(users) {
    this.suggestions = users;
    this.activeIndex = users.length ? 0 : -1;

    if (!this.hasSuggestionsTarget) {
      return;
    }

    if (!users.length) {
      this.hideSuggestions();
      return;
    }

    const items = users
      .map((user, index) => `
        <button type="button" class="mention-suggestion" data-index="${index}">
          <span class="mention-suggestion__name">${escapeHtmlUtil(user.name || 'Unknown User')}</span>
          <span class="mention-suggestion__email">${escapeHtmlUtil(user.email)}</span>
        </button>
      `)
      .join('');

    this.suggestionsTarget.innerHTML = items;
    this.suggestionsTarget.classList.remove('hidden');
    this.highlightActive();

    if (!this.boundSuggestionClick) {
      this.boundSuggestionClick = this.handleSuggestionClick.bind(this);
      this.suggestionsTarget.addEventListener('click', this.boundSuggestionClick);
    }
  }

  hideSuggestions() {
    if (!this.hasSuggestionsTarget) {
      return;
    }

    this.suggestionsTarget.classList.add('hidden');
    this.suggestionsTarget.innerHTML = '';
    this.suggestions = [];
    this.activeIndex = -1;
    this.activeMentionRange = null;
  }

  isOpen() {
    return this.hasSuggestionsTarget && !this.suggestionsTarget.classList.contains('hidden');
  }

  moveSelection(direction) {
    if (!this.suggestions.length) {
      return;
    }

    const total = this.suggestions.length;
    this.activeIndex = (this.activeIndex + direction + total) % total;
    this.highlightActive();
  }

  highlightActive() {
    if (!this.hasSuggestionsTarget) {
      return;
    }

    Array.from(this.suggestionsTarget.querySelectorAll('[data-index]')).forEach((button, index) => {
      if (index === this.activeIndex) {
        button.classList.add('is-active');
      } else {
        button.classList.remove('is-active');
      }
    });
  }

  chooseActiveSuggestion() {
    if (this.activeIndex < 0 || this.activeIndex >= this.suggestions.length) {
      this.hideSuggestions();
      return;
    }

    this.insertMentionFromIndex(this.activeIndex);
  }

  insertMentionFromIndex(index) {
    const user = this.suggestions[index];
    if (!user || !this.activeMentionRange || !this.hasDisplayTarget) {
      return;
    }

    const value = this.displayTarget.value;
    const { start, end } = this.activeMentionRange;
    const before = value.slice(0, start);
    const after = value.slice(end);

    const encoded = encodeSequence(this.sequenceRef.value++);
    const displayName = user.name || user.email || 'Unknown User';
    const mentionText = `${displayName}`;
    const placeholder = `${mentionText}${MARKER}${encoded}${MARKER} `;

    this.displayTarget.value = `${before}${placeholder}${after}`;
    const cursorPosition = before.length + placeholder.length;
    this.displayTarget.focus();
    this.displayTarget.setSelectionRange(cursorPosition, cursorPosition);

    this.mentionMap.set(encoded, { name: displayName, userId: Number.parseInt(user.id, 10) });

    this.hideSuggestions();
    this.observeQuery();
    this.syncHiddenFromDisplay();
  }

  pruneMentionMap() {
    if (!this.hasDisplayTarget || !this.mentionMap.size) {
      return;
    }

    const value = this.displayTarget.value;
    Array.from(this.mentionMap.keys()).forEach((encoded) => {
      const marker = `${MARKER}${encoded}${MARKER}`;
      if (!value.includes(marker)) {
        this.mentionMap.delete(encoded);
      }
    });
  }

  handleMentionDeletion(event) {
    if (event.key !== 'Backspace' || !this.hasDisplayTarget) {
      return false;
    }

    const input = this.displayTarget;
    if (input.selectionStart !== input.selectionEnd) {
      return false;
    }

    const cursor = input.selectionStart;
    if (cursor === 0) {
      return false;
    }

    const value = input.value;
    const uptoCursor = value.slice(0, cursor);

    let removal = this.findPlaceholderRemoval(uptoCursor, cursor);
    let removalEnd = cursor;

    if (!removal && uptoCursor.endsWith(' ')) {
      const trimmed = uptoCursor.slice(0, -1);
      removal = this.findPlaceholderRemoval(trimmed, cursor - 1);
      if (removal) {
        removalEnd = cursor;
      }
    }

    if (!removal) {
      return false;
    }

    input.value = `${value.slice(0, removal.startIndex)}${value.slice(removalEnd)}`;
    input.focus();
    input.setSelectionRange(removal.startIndex, removal.startIndex);

    this.mentionMap.delete(removal.encoded);
    event.preventDefault();
    return true;
  }

  findPlaceholderRemoval(slice, sliceLength) {
    for (const [encoded, mention] of this.mentionMap.entries()) {
      const displayName = mention.name || 'Unknown User';
      const placeholder = `${displayName}${MARKER}${encoded}${MARKER}`;
      if (slice.endsWith(placeholder)) {
        return {
          encoded,
          startIndex: sliceLength - placeholder.length
        };
      }
    }

    return null;
  }

  syncHiddenFromDisplay() {
    if (!this.hasDisplayTarget) {
      return;
    }

    const displayValue = this.displayTarget.value;
    const tokens = replacePlaceholdersWithTokens(displayValue, this.mentionMap);

    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = tokens;
    }

    this.updateHighlight(displayValue, tokens);
  }

  updateHighlight(displayValue, tokens) {
    if (!this.hasHighlightTarget) {
      return;
    }

    const hasContent = displayValue && displayValue.length > 0;

    if (!hasContent) {
      const placeholder = this.placeholder;
      this.highlightTarget.innerHTML = placeholder
        ? `<span class="comment-input-placeholder">${escapeHtmlUtil(placeholder)}</span>`
        : '&nbsp;';
      this.syncScroll();
      return;
    }

    const html = this.renderHighlight(tokens || '');
    this.highlightTarget.innerHTML = html || '&nbsp;';
    this.syncScroll();
  }

  renderHighlight(tokens) {
    if (!tokens) {
      return '';
    }

    let result = '';
    let lastIndex = 0;

    tokens.replace(TOKEN_REGEX, (match, name, id, offset) => {
      const preceding = tokens.slice(lastIndex, offset);
      result += this.escapeAndConvertNewlines(preceding);
      result += `<span class="mention mention--input" data-user-id="${id}">${escapeHtmlUtil(name)}</span>`;
      lastIndex = offset + match.length;
      return match;
    });

    result += this.escapeAndConvertNewlines(tokens.slice(lastIndex));

    return result;
  }

  escapeAndConvertNewlines(text) {
    if (!text) {
      return '';
    }

    return escapeHtmlUtil(text).replace(/\n/g, '<br>');
  }

  get placeholder() {
    if (this.hasPlaceholderValue) {
      return this.placeholderValue;
    }

    if (this.hasDisplayTarget) {
      return this.displayTarget.getAttribute('placeholder') || '';
    }

    return '';
  }

  syncScroll() {
    if (!this.hasDisplayTarget || !this.hasHighlightTarget) {
      return;
    }

    const offset = this.displayTarget.scrollTop;
    this.highlightTarget.style.transform = `translateY(-${offset}px)`;
  }
}
